/**
 * imagePollAdapter - Normalizes the image generation job status response
 * into a generic `PollUpdate` consumed by GenerationContext.
 *
 * Self-registers on module load so that GenerationContext can resume
 * polling persisted image jobs after a reload.
 *
 * The snapshot it reads comes from `GET /api/v1/jobs/{id}` — the one status
 * endpoint (integrationMode Stage 01) — via `imageGenerationAPI.getJobStatus`,
 * which is where the unified `extra_metadata.result` payload is converted into
 * `ImageJobStatus.result`. There is exactly one shape reaching this file, which
 * is why nothing below branches on which endpoint answered (RULE #-2).
 */

import { imageGenerationAPI } from '../imageGenerationAPI';
import type { ImageResult } from '../../types';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

export interface ImageJobResult {
  images: ImageResult[];
  processingTime: number;
}

export const imagePollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<ImageJobResult>> => {
  const response = await imageGenerationAPI.getJobStatus(jobId);

  if (!response.success || !response.data) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: response.error ?? 'Failed to get image job status',
        // `code` alongside the sanitized form: consumers that branch on the
        // outcome (a billing refusal must offer a purchase, not a retry) read
        // JobError.code, and it was never populated here.
        code: response.errorCode,
        sanitized: sanitizeBackendError({ error_code: response.errorCode }),
      },
    };
  }

  const data = response.data;
  const backendStatus = data.status;
  const isTerminal =
    backendStatus === 'completed' || backendStatus === 'failed' || backendStatus === 'cancelled';

  if (!isTerminal) {
    return {
      done: false,
      status: 'running',
      progress: typeof data.progress === 'number' ? data.progress : undefined,
      message: data.message,
    };
  }

  if (backendStatus === 'completed' && data.result) {
    const images = Array.isArray(data.result.images) ? data.result.images : [];
    if (images.length === 0) {
      return {
        done: true,
        status: 'failed',
        error: {
          message: 'Generation completed but no images were returned',
          sanitized: sanitizeBackendError(data),
        },
      };
    }
    return {
      done: true,
      status: 'completed',
      progress: 100,
      result: {
        images,
        processingTime: data.result.generation_time_seconds,
      },
    };
  }

  if (backendStatus === 'cancelled') {
    return {
      done: true,
      status: 'cancelled',
      error: {
        message: data.error ?? 'Image generation was cancelled',
        sanitized: sanitizeBackendError(data),
      },
    };
  }

  return {
    done: true,
    status: 'failed',
    error: {
      message: data.error ?? 'Image generation failed',
      sanitized: sanitizeBackendError(data),
    },
  };
};

registerPollAdapter('image', imagePollAdapter);
